import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Archive, Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { fetchAdminAttachmentTemplate } from '@/api/orders';
import {
  PageHeader,
  PageHeaderFooter,
  PageHeaderFooterLeft,
  PageHeaderFooterRight,
} from '@/components/shared/PageHeader';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useCountryOptions } from '@/hooks/useDropdowns';
import type { FacilityProcessGroupOption } from '@/hooks/useGroupedFacilityProcessOptions';
import { useGroupedFacilityProcessOptions } from '@/hooks/useGroupedFacilityProcessOptions';
import { useTranslator } from '@/hooks/useTranslator';
import { ATTACHMENT_TEMPLATE_TRACING_TYPES } from '@/lib/constants/attachmentTemplate';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';
import { RuleCardReadOnly } from '../../../-components/RuleCardReadOnly';
import { useAdminAttachmentTemplate } from '../../../-hooks/useAdminAttachmentTemplate';
import { useArchiveAdminAttachmentTemplate } from '../../../-hooks/useArchiveAdminAttachmentTemplate';

export const Route = createFileRoute(
  '/(client)/orders/(pages)/attachment-templates/$id/',
)({
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: ['orders', 'adminAttachmentTemplate', params.id],
      queryFn: () => fetchAdminAttachmentTemplate(params.id),
    }),
  component: AttachmentTemplateDetailPage,
});

function AttachmentTemplateDetailPage() {
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();
  const { getTranslation: t } = useTranslator({ page: 'attachmentTemplates' });
  const qc = useQueryClient();
  const sessionUser = useSessionStore(s => s.user);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const { data, error, refetch } = useAdminAttachmentTemplate(id);
  const archiveMutation = useArchiveAdminAttachmentTemplate();
  const { data: fpGroups = [] } = useGroupedFacilityProcessOptions();
  const { data: countryOptions = [] } = useCountryOptions();

  const fpLabelById = new Map<string, string>(
    (fpGroups as FacilityProcessGroupOption[]).flatMap(g =>
      g.options.map(o => [o.key, o.label]),
    ),
  );
  const countryNameByCode = new Map(countryOptions.map(c => [c.key, c.name]));

  if (error || !data) {
    return <QueryErrorState error={error} onRetry={() => void refetch()} />;
  }

  const myCompanyId = sessionUser?.companyId;
  const isShared = !data.isGlobal && data.companyId !== myCompanyId;
  const canEdit = !data.isGlobal;
  const canArchive = !data.isGlobal && !isShared;
  const tracingTypeLabel =
    ATTACHMENT_TEMPLATE_TRACING_TYPES[data.tracingType] ?? data.tracingType;

  const handleArchive = async () => {
    try {
      await archiveMutation.mutateAsync(id);
      toast.success(t('detail.archivedSuccess').replace('{{name}}', data.name));
      await qc.invalidateQueries({
        queryKey: ['orders', 'adminAttachmentTemplates'],
      });
      void navigate({ to: '/orders/attachment-templates' });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      <PageHeader>
        {/* Title row */}
        <div className='flex flex-col gap-1 min-w-0'>
          <h1 className='text-sm font-semibold text-foreground leading-tight'>
            {data.name}
          </h1>
          {data.description && (
            <p className='text-xs text-muted-foreground'>{data.description}</p>
          )}
        </div>

        {/* Footer: tracing-type badge left, actions right */}
        <PageHeaderFooter>
          <PageHeaderFooterLeft>
            <span className='inline-flex items-center px-2 py-0.5 rounded-md bg-background text-xs font-medium'>
              {tracingTypeLabel}
            </span>
          </PageHeaderFooterLeft>
          <PageHeaderFooterRight>
            {canArchive && (
              <>
                <Button
                  variant='outline'
                  onClick={() => setArchiveOpen(true)}
                  disabled={data.hasOrders}
                >
                  <Archive />
                  {t('detail.archive')}
                </Button>
                <AlertDialog open={archiveOpen}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('detail.archiveTitle')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('detail.archiveDescription').replace(
                          '{{name}}',
                          data.name,
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel
                        onClick={() => setArchiveOpen(false)}
                        disabled={archiveMutation.isPending}
                      >
                        {t('detail.archiveCancel')}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => void handleArchive()}
                        loading={archiveMutation.isPending}
                        variant='destructive'
                      >
                        {t('detail.archiveConfirm')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
            {!isShared && (
              <Button
                variant='outline'
                disabled={!canEdit}
                onClick={() =>
                  canEdit &&
                  void navigate({
                    to: '/orders/attachment-templates/$id/edit',
                    params: { id },
                  })
                }
              >
                <Pencil />
                {t('detail.edit')}
              </Button>
            )}
          </PageHeaderFooterRight>
        </PageHeaderFooter>
      </PageHeader>

      {/* Rules */}
      <div className='flex flex-col gap-4 w-[90%] max-w-4xl mx-auto px-4 sm:px-6 py-6'>
        {(data.rules ?? []).map((rule, i) => (
          <RuleCardReadOnly
            key={rule.idx ?? i}
            rule={rule}
            label={t('form.ruleLabel').replace('{{n}}', String(i + 1))}
            fpLabelById={fpLabelById}
            countryNameByCode={countryNameByCode}
          />
        ))}
        {data.backupRule && (
          <>
            {/* Backup separator */}
            <div className='flex items-center gap-3 pt-2'>
              <div className='flex-1 border-t border-dashed border-border/60' />
              <span className='text-xs text-muted-foreground/60 uppercase tracking-widest shrink-0'>
                {t('form.backupRuleSeparator')}
              </span>
              <div className='flex-1 border-t border-dashed border-border/60' />
            </div>
            <RuleCardReadOnly
              rule={data.backupRule}
              label={t('form.backupRuleFullTitle')}
              isBackup
              fpLabelById={fpLabelById}
              countryNameByCode={countryNameByCode}
            />
          </>
        )}
        {(data.rules ?? []).length === 0 && !data.backupRule && (
          <p className='text-xs text-muted-foreground py-4'>
            {t('detail.noRules')}
          </p>
        )}
      </div>
    </div>
  );
}
